/**
 * The Email Template composer (piece 5). It inserts merge-field chips that
 * resolve per record and auto-attaches the source document as a portable
 * document format (PDF) file.
 *
 * A chip resolves against three records and no others: the document the send
 * is about, that document's Company, and the recipient contact.
 */
export type ChipScope = 'document' | 'company' | 'contact';

export interface MergeField {
  key: string;
  scope: ChipScope;
}

/** The starting set. A chip outside it names a record the composer does not read. */
export const MERGE_FIELDS: readonly MergeField[] = [
  { key: 'document.number', scope: 'document' },
  { key: 'document.date', scope: 'document' },
  { key: 'document.total', scope: 'document' },
  { key: 'document.dueDate', scope: 'document' },
  { key: 'company.name', scope: 'company' },
  { key: 'company.billingAddress', scope: 'company' },
  { key: 'contact.firstName', scope: 'contact' },
  { key: 'contact.fullName', scope: 'contact' },
];

const CHIP = /\{\{\s*([A-Za-z][A-Za-z0-9_.]*)\s*\}\}/g;

export interface ComposeContext {
  document: Readonly<Record<string, string | null>>;
  company: Readonly<Record<string, string | null>>;
  contact: Readonly<Record<string, string | null>>;
}

export class EmailComposerError extends Error {}

export function chipsIn(text: string): string[] {
  return [...text.matchAll(CHIP)].map((m) => m[1]!);
}

export function isKnownChip(key: string): boolean {
  return MERGE_FIELDS.some((f) => f.key === key);
}

function valueOf(key: string, context: ComposeContext): string | null {
  const [scope, ...rest] = key.split('.');
  const field = rest.join('.');
  if (scope === 'document') return context.document[field] ?? null;
  if (scope === 'company') return context.company[field] ?? null;
  if (scope === 'contact') return context.contact[field] ?? null;
  return null;
}

export interface ChipResolution {
  text: string;
  /** Chips whose value is missing, and the chips this composer does not know. */
  missing: string[];
  unknown: string[];
}

export function resolveChips(text: string, context: ComposeContext): ChipResolution {
  const missing: string[] = [];
  const unknown: string[] = [];
  const resolved = text.replace(CHIP, (_whole, key: string) => {
    if (!isKnownChip(key)) {
      unknown.push(key);
      return _whole as string;
    }
    const value = valueOf(key, context);
    if (value === null || value === '') {
      missing.push(key);
      return _whole as string;
    }
    return value;
  });
  return { text: resolved, missing, unknown };
}

export interface ComposedMessage {
  to: string;
  subject: string;
  body: string;
  /** Decision 0569: the template's own address, else whoever is sending; null when neither is known. */
  from: string | null;
  cc: string[];
  bcc: string[];
}

/**
 * A chip that resolves to nothing blocks the send and names the field, rather
 * than rendering an empty line: the reminder engine sends these same bodies
 * unattended, so an empty chip reaches a customer with nobody reading it
 * first.
 */
export function sendBlocked(message: ComposedMessage, context: ComposeContext): { blocked: boolean; fields: string[] } {
  const subject = resolveChips(message.subject, context);
  const body = resolveChips(message.body, context);
  const fields = [...new Set([...subject.missing, ...body.missing, ...subject.unknown, ...body.unknown])];
  return { blocked: fields.length > 0, fields };
}

export function assertSendable(message: ComposedMessage, context: ComposeContext): void {
  const outcome = sendBlocked(message, context);
  if (outcome.blocked) {
    throw new EmailComposerError(`the send is held: ${outcome.fields.join(', ')} resolved to nothing, and no message goes out with an empty line where a chip stood (piece 5)`);
  }
}

export interface EmailTemplateRow {
  id: string;
  name: string;
  documentType: string;
  isDefault: boolean;
  subject: string;
  body: string;
  /** Decision 0569: one of the organisation's own sending addresses; null sends as whoever sends it. */
  fromAddress: string | null;
  /** Decision 0569: addresses copied on every send of this template. */
  ccAddresses: readonly string[];
  /** Decision 0569: addresses blind-copied on every send of this template. */
  bccAddresses: readonly string[];
}

/**
 * Subject and Message Body pre-fill from the chosen template, and To from the
 * record the send is about — the Company the document names and the contact
 * chosen on it — because `EmailTemplate` carries no recipient column. From is
 * the template's own address when it names one, else the address of whoever
 * is sending; Cc and Bcc come straight from the template (decision 0569).
 */
export function prefill(template: EmailTemplateRow, recipientEmail: string | null, senderOwnEmail: string | null = null): ComposedMessage {
  return {
    to: recipientEmail ?? '',
    subject: template.subject,
    body: template.body,
    from: template.fromAddress ?? senderOwnEmail,
    cc: [...template.ccAddresses],
    bcc: [...template.bccAddresses],
  };
}

/** Editing any field changes only this send, never the template. */
export function editIsLocalToTheSend(template: EmailTemplateRow, edited: ComposedMessage): EmailTemplateRow {
  void edited;
  return template;
}

/** The manager allows one Default per document type. */
export function defaultFor(templates: readonly EmailTemplateRow[], documentType: string): EmailTemplateRow | undefined {
  return templates.find((t) => t.documentType === documentType && t.isDefault);
}

/** The source document goes out attached as a PDF, without being asked for. */
export const AUTO_ATTACHES_SOURCE_PDF = true;
